import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts'
import { useATMStore } from '../store/atmStore'
import { MOCK_AVAILABILITY_TREND, MOCK_TOP_ERRORS } from '../mockData'
import SimulationControl from './SimulationControl'
import { useTheme } from '../hooks/useTheme'

const ZONE_COLORS = ['#02AFF0', '#264E72', '#F89421', '#22c55e']

export default function KPIDashboard() {
  const C = useTheme()
  const STATUS_COLORS: Record<string, string> = {
    IN_SERVICE: C.green, OUT_OF_SERVICE: C.red,
    WARNING: C.orange, SUPERVISOR: C.blue, UNKNOWN: '#6b7280', LOADING: C.yellow,
  }
  const { kpis, simulationRun } = useATMStore()
  const [showSim, setShowSim] = useState(false)

  const statusPieData = kpis
    ? (() => {
        const inService  = kpis.in_service  ?? kpis.online_atms ?? 0
        const total      = kpis.total_atms  ?? 0
        const outOfSvc   = kpis.out_of_service ?? Math.max(0, total - inService)
        const warning    = kpis.warning     ?? 0
        const supervisor = kpis.supervisor  ?? 0
        return [
          { name: 'En Servicio',    value: inService,  color: C.green  },
          { name: 'Fuera Servicio', value: outOfSvc,   color: C.red    },
          { name: 'Advertencia',    value: warning,    color: C.orange },
          { name: 'Supervisor',     value: supervisor, color: C.blue   },
        ].filter((d) => (d.value ?? 0) > 0)
      })()
    : []

  const zoneBarData = kpis
    ? (Array.isArray(kpis.by_zone) ? kpis.by_zone : Object.values(kpis.by_zone)).map((z) => ({
        zone: z.zone.replace('Distrito Nacional', 'D.N.'),
        disponibilidad: +((z.availability_pct ?? 0).toFixed(1)),
        disponible: z.online_atms ?? z.available ?? 0,
        total: z.total_atms ?? z.total ?? 0,
      }))
    : []

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 14, background: C.dark, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <SummaryCard label="Total ATMs" value={kpis?.total_atms ?? 933} unit="" color={C.blue} icon="⊞" />
        <SummaryCard label="Disponibilidad" value={kpis?.availability_pct?.toFixed(1) ?? '90.8'} unit="%" color={C.green} icon="✓" />
        <SummaryCard label="Disponibilidad Financiera" value={kpis?.financial_availability_pct?.toFixed(1) ?? '92.3'} unit="%" color='#a3e635' icon="$" />
        <SummaryCard label="Alertas Activas" value={kpis?.active_alerts ?? 37} unit="" color={C.orange} icon="⚠" />
        <SummaryCard label="MTTR" value={kpis?.mttr_minutes ?? 148} unit=" min" color={C.yellow} icon="⏱" />
        <SummaryCard label="TX / hora" value={kpis?.tx_per_hour?.toLocaleString('es-DO') ?? '4,820'} unit="" color={C.text} icon="↑" />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Zone Availability Bar */}
        <ChartCard title="Disponibilidad por Zona" badge={`${Array.isArray(kpis?.by_zone) ? kpis!.by_zone.length : Object.keys(kpis?.by_zone ?? {}).length} zonas`}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={zoneBarData} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="zone" tick={{ fill: C.muted, fontSize: 11 }} />
              <YAxis domain={[80, 100]} tick={{ fill: C.muted, fontSize: 11 }} unit="%" />
              <Tooltip
                contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: C.text }} formatter={(v: number) => [`${v}%`, 'Disponibilidad']}
              />
              <Bar dataKey="disponibilidad" radius={[4, 4, 0, 0]}>
                {zoneBarData.map((_, i) => (
                  <Cell key={i} fill={ZONE_COLORS[i % ZONE_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Status Pie */}
        <ChartCard title="Distribución de Estados" badge={`${kpis?.total_atms ?? 933} ATMs`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                  paddingAngle={2} dataKey="value">
                  {statusPieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
                  formatter={(v: number) => [v, 'ATMs']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {statusPieData.map((d) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                  <span style={{ color: C.muted, flex: 1 }}>{d.name}</span>
                  <span style={{ color: C.text, fontWeight: 600 }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
        {/* Availability Trend */}
        <ChartCard title="Tendencia Disponibilidad — Últimas 24h" badge="Tiempo simulado">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={MOCK_AVAILABILITY_TREND} margin={{ top: 6, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="time" tick={{ fill: C.muted, fontSize: 9 }} interval={3} />
              <YAxis domain={[83, 99]} tick={{ fill: C.muted, fontSize: 11 }} unit="%" />
              <Tooltip
                contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                labelStyle={{ color: C.text }}
              />
              <Legend wrapperStyle={{ fontSize: 10, color: C.muted }} />
              <Line type="monotone" dataKey="dn" name="D.N." stroke={C.blue} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="norte" name="Norte" stroke={C.orange} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="sur" name="Sur" stroke='#a78bfa' dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="este" name="Este" stroke={C.green} dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Simulation Status */}
        <ChartCard title="Control de Simulación" badge={simulationRun?.state ?? simulationRun?.status ?? 'Sin ejecutar'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {simulationRun ? (
              <>
                <StatusBadge label="Estado" value={simulationRun.state ?? simulationRun.status ?? '—'} />
                <StatusBadge label="Eventos" value={(simulationRun.events_emitted ?? simulationRun.events_generated ?? 0).toLocaleString()} />
                <StatusBadge label="Alertas" value={(simulationRun.alerts_triggered ?? simulationRun.events_processed ?? 0).toLocaleString()} />
                <StatusBadge label="Escenario" value={simulationRun.config.scenario.replace(/_/g, ' ')} />
              </>
            ) : (
              <div style={{ color: C.muted, fontSize: 12, fontStyle: 'italic', padding: '20px 0' }}>
                No hay simulación activa.
              </div>
            )}
            <button
              onClick={() => setShowSim((v) => !v)}
              style={{
                marginTop: 'auto', background: C.primary, border: `1px solid ${C.blue}`,
                color: '#fff', borderRadius: 6, padding: '7px 12px', cursor: 'pointer',
                fontSize: 12, fontFamily: 'inherit', fontWeight: 600,
              }}
            >
              ⚙ Abrir Simulador
            </button>
          </div>
        </ChartCard>
      </div>

      {/* Top Errors Bar Chart */}
      <ChartCard title="Top 10 Errores por Frecuencia" badge="Códigos canónicos">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={MOCK_TOP_ERRORS} layout="vertical" margin={{ top: 4, right: 20, left: 80, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
            <XAxis type="number" tick={{ fill: C.muted, fontSize: 11 }} />
            <YAxis dataKey="code" type="category" tick={{ fill: C.text, fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }} width={90} />
            <Tooltip
              contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, _n: string, props) => [v, `${props.payload.description}`]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} name="Ocurrencias">
              {MOCK_TOP_ERRORS.map((entry, i) => (
                <Cell key={i} fill={
                  entry.severity === 'CRITICAL' ? C.red :
                  entry.severity === 'HIGH' ? C.orange :
                  entry.severity === 'WARNING' ? C.yellow : C.blue
                } />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Simulation Control Modal */}
      {showSim && createPortal(
        <SimModal onClose={() => setShowSim(false)} />,
        document.body,
      )}
    </div>
  )
}

function SummaryCard({ label, value, unit, color, icon }: {
  label: string; value: string | number; unit: string; color: string; icon: string
}) {
  const C = useTheme()
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            {label}
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>
            {value}{unit}
          </div>
        </div>
        <span style={{ fontSize: 18, color, opacity: 0.5 }}>{icon}</span>
      </div>
    </div>
  )
}

function ChartCard({ title, badge, children }: { title: string; badge?: string; children: React.ReactNode }) {
  const C = useTheme()
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>
          {title}
        </span>
        {badge && (
          <span style={{
            background: 'rgba(2,175,240,0.12)', color: C.blue, padding: '2px 8px',
            borderRadius: 999, fontSize: 10,
          }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

function StatusBadge({ label, value }: { label: string; value: string | number }) {
  const C = useTheme()
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
      <span style={{ color: C.muted }}>{label}</span>
      <span style={{ color: C.text, fontWeight: 600 }}>{value}</span>
    </div>
  )
}

function SimModal({ onClose }: { onClose: () => void }) {
  const C = useTheme()
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 900,
          maxHeight: '90vh', overflowY: 'auto',
          background: '#1a2230',
          border: '1px solid rgba(2,175,240,0.4)',
          borderRadius: 12,
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
          position: 'relative',
        }}
      >
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(38,78,114,0.6)',
          borderRadius: '12px 12px 0 0',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <span style={{ color: '#02AFF0', fontWeight: 700, fontSize: 13, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            ⚙ Control de Simulación
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
              color: '#e7ecf3', borderRadius: 6, width: 28, height: 28,
              cursor: 'pointer', fontSize: 16, lineHeight: 1, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
        <SimulationControl />
      </div>
    </div>
  )
}
